import {inject, injectable} from "inversify";
import type {IApiResponse, IHttpClientService} from "data/services/http";
import type {AxiosResponse} from "axios";
import {Bindings} from "data/constants/bindings";
import {IContestRankingsPayload, IRankingsResponse, IRankingsProfile} from "data/types/rankings";
import {
	IRoundRankingsPayload,
	IRoundRankingsSummaryPayload,
	IRoundRankingsSummaryResponse,
} from "data/stores/round_summary/round_summary.store";
import {IRankingsProfilePayload} from "data/stores/user_roster/user_roster.store";

export type TRankingsResponse = IApiResponse<IRankingsResponse>;
export type TRankingsSummaryResponse = IApiResponse<IRoundRankingsSummaryResponse>;

export interface IRankingsApiProvider {
	contestRankings: (
		payload: IContestRankingsPayload
	) => Promise<AxiosResponse<TRankingsResponse>>;
	roundRankings: (payload: IRoundRankingsPayload) => Promise<AxiosResponse<TRankingsResponse>>;
	roundSummary: (
		payload: IRoundRankingsSummaryPayload
	) => Promise<AxiosResponse<TRankingsSummaryResponse>>;
	profile: (
		payload: IRankingsProfilePayload
	) => Promise<AxiosResponse<IApiResponse<IRankingsProfile>>>;
}

@injectable()
export class RankingsApiProvider implements IRankingsApiProvider {
	constructor(@inject(Bindings.ApiHTTPClient) private _http: IHttpClientService) {}

	public contestRankings = (payload: IContestRankingsPayload) => {
		const {contestId, ...rest} = payload;
		return this._http.get<TRankingsResponse>(`ranking/contest-leaderboard/${contestId}`, rest);
	};

	public roundRankings = (payload: IRoundRankingsPayload) => {
		const {roundId, ...rest} = payload;
		return this._http.get<TRankingsResponse>(`ranking/round-leaderboard/${roundId}`, rest);
	};

	public roundSummary = (payload: IRoundRankingsSummaryPayload) => {
		const {roundId} = payload;
		return this._http.get<TRankingsSummaryResponse>(`ranking/round-summary/${roundId}`);
	};

	public profile = (payload: IRankingsProfilePayload) => {
		const {contestId, userId} = payload;
		return this._http.get<IApiResponse<IRankingsProfile>>(
			`ranking/profile/${contestId}/${userId}`
		);
	};
}
